/**
 * Version Checker Utility
 * Monitors for new app versions and prompts user to refresh when a new version is deployed
 */

const VERSION_CHECK_INTERVAL = 10 * 1000; // Check every 1 minutes
const LOCAL_STORAGE_VERSION_KEY = "app_version";

let checkInterval = null;

/**
 * Fetch the current version from the version.json file
 * @returns {Promise<string>} The current app version
 */
export const fetchCurrentVersion = async () => {
  try {
    const response = await fetch(
      `${window.location.origin}/version.json?t=${Date.now()}`,
      {
        cache: "no-store",
        headers: {
          "Cache-Control": "no-cache, no-store, must-revalidate",
          Pragma: "no-cache",
          Expires: "0",
        },
      }
    );

    if (!response.ok) {
      throw new Error(`Failed to fetch version: ${response.status}`);
    }

    const versionData = await response.json();
    return versionData.version;
  } catch (error) {
    console.error("Error fetching version:", error);
    return null;
  }
};

/**
 * Get the locally stored version (from previous app load)
 * @returns {string|null} The stored version or null if not found
 */
export const getStoredVersion = () => {
  return localStorage.getItem(LOCAL_STORAGE_VERSION_KEY);
};

/**
 * Store the current version in localStorage
 * @param {string} version - The version to store
 */
export const storeVersion = (version) => {
  if (version) {
    localStorage.setItem(LOCAL_STORAGE_VERSION_KEY, version);
  }
};

/**
 * Start monitoring for version changes
 * @param {Function} onNewVersion - Callback function to trigger when new version detected
 * @returns {Function} Function to stop monitoring
 */
export const startVersionCheck = (onNewVersion) => {
  if (!onNewVersion || typeof onNewVersion !== "function") {
    console.warn("onNewVersion callback must be a function");
    return () => {};
  }

  // Check immediately on first load
  const checkVersion = async () => {
    const currentVersion = await fetchCurrentVersion();
    const storedVersion = getStoredVersion();

    if (currentVersion && storedVersion && currentVersion !== storedVersion) {
      console.log(
        `New version detected: ${storedVersion} -> ${currentVersion}`
      );
      onNewVersion(currentVersion, storedVersion);
    } else if (currentVersion && !storedVersion) {
      // First time: store the version
      storeVersion(currentVersion);
    }
  };

  // Check immediately
  checkVersion();

  // Set up periodic checks
  checkInterval = setInterval(checkVersion, VERSION_CHECK_INTERVAL);

  // Return function to stop checking
  return () => {
    if (checkInterval) {
      clearInterval(checkInterval);
      checkInterval = null;
    }
  };
};

/**
 * Stop version monitoring
 */
export const stopVersionCheck = () => {
  if (checkInterval) {
    clearInterval(checkInterval);
    checkInterval = null;
  }
};

/**
 * Clear cached version (useful for testing or forced refresh)
 */
export const clearCachedVersion = () => {
  localStorage.removeItem(LOCAL_STORAGE_VERSION_KEY);
};

/**
 * Hard refresh the page to load new version
 */
export const refreshPage = () => {
  // Clear all caches and reload
  window.location.href = window.location.pathname;
};
